// Which /weeds catalog entries are themselves grasses (Poaceae).
//
// There is no taxonomy field to derive this from: `WeedCharacteristics`
// (data/weed-types.ts) carries timing, region, difficulty and free-text notes
// only, and the editorial pages in data/weeds/index.ts are prose. Rather than
// pattern-match the notes ("Warm-season annual grass") — which would silently
// misclassify the moment an editor rewrites a sentence — the split is an
// explicit slug list here, keyed to WEED_PAGE_SLUGS.
//
// Membership is botanical family, not the "-grass" suffix in the common name:
// Yellow Nutsedge is a sedge (Cyperaceae) and is deliberately absent, so it
// keeps the generic weed framing.
//
// A test asserts every slug below exists in WEED_PAGE_SLUGS, so a renamed or
// removed weed page cannot leave a stale entry behind.

import { slugifyConditionName } from '@/data/condition-product-picks'
import { resolveWeedAlias } from '@/data/weed-registry'

export const GRASS_FAMILY_WEED_SLUGS: readonly string[] = [
  'crabgrass',
  'foxtail-setaria',
  'goosegrass',
  'annual-bluegrass-poa-annua',
  'quackgrass',
  'orchardgrass',
  'creeping-bentgrass',
]

const GRASS_FAMILY_WEED_SLUG_SET = new Set(GRASS_FAMILY_WEED_SLUGS)

/** True when the weed slug names a plant in the grass family (Poaceae). */
export function isGrassFamilyWeedSlug(slug: string | null | undefined): boolean {
  return Boolean(slug) && GRASS_FAMILY_WEED_SLUG_SET.has(slug as string)
}

/** Same test from a model-emitted weed name.
 *
 * Goes through `resolveWeedAlias` first, because the model's common name is
 * often not the catalog slug: it emits "Foxtail" for `foxtail-setaria` and
 * "Poa annua" for `annual-bluegrass-poa-annua`. A bare slugify misses both,
 * and those two are 48 of the 362 named weed reroutes in a 28-day sample, so
 * the naive form silently withheld this branch from the second most common
 * grassy weed we identify. The slugify path stays as a fallback for a name
 * the registry does not alias. */
export function isGrassFamilyWeedName(name: string | null | undefined): boolean {
  if (!name) return false
  const aliased = resolveWeedAlias(name)?.guideSlug
  if (aliased) return isGrassFamilyWeedSlug(aliased)
  return isGrassFamilyWeedSlug(slugifyConditionName(name))
}
